import { useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { CreateFolderModal } from '@/components/CreateFolder';
import { DOWNLOAD_STORAGE_KEY } from '@/components/FastCheckoutModal/constants';
import { useHasActiveSubscription } from '@/hooks/user';
import { SUBSCRIPTION_STATUS } from '@/models/subscription';
import routes from '@/routes';
import { clearData, setCreateFolderModal } from '@/store/myDocuments';
import { fetchUser } from '@/store/profile/thunks';
import { storage } from '@/utils/browser';

import CreateNewButton from './CreateNewButton';
import { DailyLimitModalListener } from './DailyLimitModal';
import DocumentSection from './DocumentSection';
import FolderSection from './FolderSection';
import LimitedPlan from './LimitedPlan';
import { MinutesHelper } from './MinutesHelper';
import ModalTimer from './ModalTimer';
import RateUs from './RateUs';

function MyDocuments() {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const hasActiveSubscription = useHasActiveSubscription();
	const navigate = useNavigate();

	const createFolderModal = useSelector(
		state => state.myDocuments.createFolderModal
	);

	useEffect(() => {
		if (searchParams.get('success')) {
			(async () => {
				const response = await dispatch(fetchUser());

				if (
					![
						SUBSCRIPTION_STATUS.ACTIVE,
						SUBSCRIPTION_STATUS.TRIALING
					].includes(response.payload?.subscription?.status)
				) {
					navigate(routes.billing);
				}
			})();
		}

		setSearchParams({});

		return () => {
			dispatch(clearData());
			storage.remove(DOWNLOAD_STORAGE_KEY);
		};
	}, []);

	return (
		<div className="flex max-w-[94.5rem] flex-col justify-center gap-9 px-5 pb-12 md:gap-10 md:px-8 lg:gap-10">
			<div className="mt-[56px] flex items-center justify-between">
				<h1 className="font-title text-xl font-semibold leading-8 lg:text-[32px] lg:leading-10">
					{t('myDocuments.title')}
				</h1>

				<CreateNewButton />
			</div>

			<RateUs />

			<LimitedPlan />

			<FolderSection section="folders" />

			{!hasActiveSubscription && <MinutesHelper />}
			<DocumentSection section="documents" />

			<DailyLimitModalListener />

			<CreateFolderModal
				open={createFolderModal}
				onClose={() => dispatch(setCreateFolderModal(false))}
			/>

			<ModalTimer />
		</div>
	);
}

export default MyDocuments;
